import { cn } from '@/lib/utils';
import { SparkleIcon } from '@/components/ui/icons';

/**
 * The face Itsappy AI answers with.
 *
 * People in this app are a circle with their picture or their colour in it,
 * and the assistant needs to sit in that row without pretending to be one of
 * them. Hence the same circle on the same size scale — it lines up in a
 * thread, an avatar stack, a header — filled with the brand gradient rather
 * than a person's colour, and marked with the sparkle the product already
 * uses everywhere it means "this was the model".
 *
 * A gradient rather than a flat fill because the fill is the identity here.
 * A person's circle carries a photograph or the colour their account was
 * given; this one has neither, and a single brand-500 disc is indistinguishable
 * from a member whose colour happens to be indigo.
 */
const SIZES = {
  xs: { box: 'h-5 w-5', glyph: 'h-2.5 w-2.5' },
  sm: { box: 'h-7 w-7', glyph: 'h-3.5 w-3.5' },
  md: { box: 'h-9 w-9', glyph: 'h-[1.125rem] w-[1.125rem]' },
  lg: { box: 'h-12 w-12', glyph: 'h-6 w-6' },
  xl: { box: 'h-20 w-20', glyph: 'h-10 w-10' },
};

export function AssistantAvatar({
  size = 'md',
  thinking = false,
  className,
}: {
  size?: keyof typeof SIZES;
  /**
   * Breathes while an answer is on its way. The wait already has three
   * settling dots next to it; this makes the face itself part of the same
   * signal, so a thread scrolled away from the composer still shows that
   * something is coming.
   */
  thinking?: boolean;
  className?: string;
}) {
  const { box, glyph } = SIZES[size];
  return (
    <span
      // Decorative: every place this is used already names the speaker in text
      // beside it, and a second "Itsappy AI" for a screen reader is noise.
      aria-hidden
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center rounded-full',
        'bg-gradient-to-br from-brand-400 via-brand-500 to-violet-500',
        // Lifts the disc off both themes' surfaces without a border, which at
        // 20px would read as a ring rather than an edge.
        'shadow-sm shadow-brand-500/25',
        box,
        className,
      )}
    >
      {thinking && (
        <span className="absolute inset-0 animate-ping rounded-full bg-brand-400/40 motion-reduce:hidden" />
      )}
      {/* Literal white: the disc under it is the brand gradient in both
          themes, not a themed surface that flips with the palette. */}
      <SparkleIcon className={cn('relative text-white', glyph)} />
    </span>
  );
}
